<script lang="ts">
	import { format } from 'svelte-i18n';
		import { getDeviceStatus, getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import type { Device } from 'src/types/Device';

	import {
		Button,
		ButtonGroupV2,
		ButtonV2,
		Icon,
		Text,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import type { CameraZoneType } from '../../types/camera-zones';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		addCameraZone,
		deleteCameraZone,
		enableCameraZone,
		getCameraZonesList,
		getZoneId,
		updateCameraZone,
	} from 'src/components/Camera/helpers/camera-zone-helpers';
	import ZonesListItem from './ZonesListItem.svelte';
	import ZonesListItemV2 from './ZonesListItemV2.svelte';
	import AddEditZoneModal from './AddEditZoneModal.svelte';
	import DeleteZoneModal from './DeleteZoneModal.svelte';
	import { getSidepanelTabNavigationContext } from 'src/components/Sidepanel/sidepanel-tab-navigation-context';

	const isSidepanelContext = !!getSidepanelTabNavigationContext();

	export let device: Device;
	export let existingMediaStream: MediaStream = null;

	const realId = real_id(device.id);
	let editZoneModal = false;
	let showDeleteModal = false;
	let activeZoneType: CameraZoneType = 'motion';

	let selectedZone,
		selectedZoneId = null;

	let loadingComponent: string | null = null;
	$: settingsStore = getDeviceSettings(realId);
	$: device_settings = $settingsStore;
	$: statusStore = getDeviceStatus(realId);
	$: device_status = $statusStore;

	const setSelectedZone = (zone_component) => {
		selectedZone = zone_component;
		selectedZoneId = getZoneId(zone_component);
	};

	let editZoneModalRef: AddEditZoneModal;

	const handleSave = async (event) => {
		let data = event.detail;
		let res = null;
		if (data?.zone_id) {
			res = await updateCameraZone(device, data.zone_id, data.zone);
		} else {
			res = await addCameraZone(device, data.zone);
		}

		if (res?.error) {
			const message = res.error?.device_error?.message || res.error?.error;
			editZoneModalRef?.onSaveError(message);
			return;
		}

		toast.open({
			type: 'success',
			title: $format('zone_save_scc_toast', { default: 'Zone saved successfully.' }),
		});

		if (res && res?.data?.zone_id !== undefined) {
			const currentSettings = readDeviceSettings(realId) ?? {};
			devices_settings.update_devices_settings(realId, {
				...currentSettings,
				['camerazone:' + res.data.zone_id]: data.zone,
			});
			zones = getCameraZonesList(readDeviceSettings(realId));
		}
		editZoneModal = false;
	};

	const handleDelete = async (event) => {
		let data = event.detail;
		loadingComponent = 'camerazone:' + data.zone_id;
		await deleteCameraZone(device, data.zone_id)
			.then(() => {
				showDeleteModal = false;
				setSelectedZone(null);
				zones = zones.filter((zone) => zone !== 'camerazone:' + data.zone_id);
				toast.open({
					type: 'success',
					title: $format('zone_delete_scc_toast', { default: 'Zone deleted successfully.' }),
				});
			})
			.catch(() => {
				toast.open({ type: 'danger', title: 'Error deleting zone.' });
			});
		loadingComponent = null;
	};

	const handleEnable = async (event) => {
		const { zone_id, enable } = event.detail;
		const component = 'camerazone:' + zone_id;
		if (loadingComponent === component) return;

		const previousSettings = readDeviceSettings(realId)?.[component];

		loadingComponent = component;
		devices_settings.update_devices_settings(realId, {
			...(readDeviceSettings(realId) ?? {}),
			[component]: { ...previousSettings, enable },
		});

		const res = await enableCameraZone(device, zone_id, enable);

		if (!res?.isok) {
			devices_settings.update_devices_settings(realId, {
				...(readDeviceSettings(realId) ?? {}),
				[component]: previousSettings,
			});
			toast.open({ type: 'danger', title: 'Error updating zone.' });
		}
		loadingComponent = null;
	};

	$: zones = getCameraZonesList(device_settings);
	$: filteredZones = zones.filter(
		(zone_component) => device_settings?.[zone_component]?.type === activeZoneType,
	);
	$: addButtonLabel =
		activeZoneType === 'privacy'
			? $format('button_add_privacy_zone', { default: 'Add privacy zone' })
			: $format('button_add_detection_zone', { default: 'Add detection zone' });
	$: emptyStateTitle =
		activeZoneType === 'privacy'
			? $format('zones_create_privacy_zone', { default: 'Create a privacy zone' })
			: $format('zones_create_motion_zone', { default: 'Create a motion zone' });
	$: emptyStateDescription =
		activeZoneType === 'privacy'
			? $format('zones_no_privacy_zones_description', {
					default: 'Privacy zones will hide selected areas from the camera feed.',
				})
			: $format('zones_no_motion_zones_description', {
					default: 'Motion zones will detect movement in selected areas.',
				});
</script>

<div class="mb-s">
	<ButtonGroupV2
		aria-label={$format('label_zone_type', { default: 'Zone type' })}
		noWrap
		class="width-full"
	>
		<Button
			class={`width-full ${activeZoneType === 'motion' ? 'active' : ''}`}
			size="m"
			on:click={() => (activeZoneType = 'motion')}
		>
			{$format('zone_type_motion', { default: 'Motion zones' })}
		</Button>
		<Button
			class={`width-full ${activeZoneType === 'privacy' ? 'active' : ''}`}
			size="m"
			on:click={() => (activeZoneType = 'privacy')}
		>
			{$format('zone_type_privacy', { default: 'Privacy zones' })}
		</Button>
	</ButtonGroupV2>
</div>

{#if filteredZones.length === 0}
	<div class="flex-full-height align-items-center pt-l">
		<Icon
			class="mb-m"
			size="l"
			icon={activeZoneType === 'privacy' ? 'fas fa-video-slash' : 'fa-light fa-vector-square'}
		/>
		<Text styleType="body-small" class="mb-s text-bold text-center">
			{emptyStateTitle}
		</Text>
		<Text class="text-color-muted text-center" styleType="body-extra-small">
			{emptyStateDescription}
		</Text>
	</div>
{/if}

{#each filteredZones as zone_component}
	{@const zoneProps = {
		zone_component,
		zone_settings: device_settings?.[zone_component],
		zone_status: device_status?.[zone_component],
		disableToggle: loadingComponent === zone_component,
	}}
	{#if isSidepanelContext}
		<ZonesListItemV2
			{...zoneProps}
			on:enable={handleEnable}
			on:edit={() => {
				editZoneModal = true;
				setSelectedZone(zone_component);
			}}
			on:delete={() => {
				showDeleteModal = true;
				setSelectedZone(zone_component);
			}}
		/>
	{:else}
		<ZonesListItem
			{...zoneProps}
			on:enable={handleEnable}
			on:edit={() => {
				editZoneModal = true;
				setSelectedZone(zone_component);
			}}
			on:delete={() => {
				showDeleteModal = true;
				setSelectedZone(zone_component);
			}}
		/>
	{/if}
{/each}

<div class="pt-s">
	{#if isSidepanelContext}
		<ButtonV2
			size="m"
			width="full"
			on:click={() => {
				editZoneModal = true;
				setSelectedZone(null);
			}}
		>
			{addButtonLabel}
		</ButtonV2>
	{:else}
		<Button
			size="m"
			width="full"
			disabled={false}
			on:click={() => {
				editZoneModal = true;
				setSelectedZone(null);
			}}
		>
			{addButtonLabel}
		</Button>
	{/if}
</div>

{#if editZoneModal}
	<AddEditZoneModal
		bind:this={editZoneModalRef}
		{device}
		{existingMediaStream}
		showModal={editZoneModal}
		zone_id={selectedZoneId}
		zone_settings={device_settings?.[selectedZone]}
		zoneType={activeZoneType}
		defaultColorIndex={selectedZone ? null : zones.length}
		on:close={() => {
			editZoneModal = false;
		}}
		on:save={handleSave}
	/>
{/if}

{#if showDeleteModal}
	<DeleteZoneModal
		showModal={showDeleteModal}
		zone_id={selectedZoneId}
		zone_settings={device_settings?.[selectedZone]}
		isLoading={loadingComponent !== null}
		on:close={() => {
			showDeleteModal = false;
		}}
		on:delete={handleDelete}
	/>
{/if}
